import * as React from 'react'
import { Link } from 'react-router-dom'
import './Button.scss'

export interface IButtonProps {
  /**
   * Add custom className
   */
  className?: string
  /**
   * Button type
   */
  type?: 'button' | 'submit' | 'reset'
  /**
   * Either use this prop or set children directly
   */
  text?: string
  /**
   * If button should look like a link
   */
  link?: boolean
  /**
   * Use a react router link component instead. NB! Using together with "externalLink" prop the native <a> element will
   * be used instead
   */
  url?: string
  /**
   * Use an <a> tag instead (for external links). NB! Use together with the "url" prop
   */
  externalLink?: boolean
  /**
   * Block level button
   */
  block?: boolean
  /**
   * Smaller font size
   */
  smaller?: boolean
  /**
   * Blue text color
   */
  blue?: boolean
  /**
   * Optional left icon
   */
  //iconLeft?: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | undefined
  iconLeft?: any
  /**
   * Optional right icon
   */
  //iconRight?: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | undefined
  iconRight?: any
  /**
   * Click callback
   */
  onClick?(): void

  data?: object
  children?: React.ReactNode
  disabled?: boolean
  taller?: boolean
  darkText?: boolean
  /**
   * ARIA role override. Use `role="link"` when rendering a <button> that navigates,
   * so screen readers announce it as a link rather than a button.
   */
  role?: string
  /**
   * Expanded state for disclosure buttons (accordions, dropdowns).
   */
  'aria-expanded'?: boolean
  /**
   * Id of the element this button controls.
   */
  'aria-controls'?: string
}

export class Button extends React.PureComponent<IButtonProps> {
  static defaultProps: IButtonProps = {
    type: 'button',
  }

  BEM = () => {
    const { block, link, smaller, taller, blue, darkText, className } = this.props
    const classArray = ['button']

    if (link) {
      classArray.push('button--link')
    }

    if (className) {
      classArray.push(className)
    }

    if (block) {
      classArray.push('button--block')
    }

    if (smaller) {
      classArray.push('button--smaller')
    }

    if (taller) {
      classArray.push('button--taller')
    }

    if (blue) {
      classArray.push('button--blue')
    }

    if (darkText) {
      classArray.push('button--dark-text')
    }

    return classArray.join(' ')
  }

  handleClick = () => {
    if (this.props.onClick) {
      this.props.onClick()
    }
  }

  renderInnerButton = () => {
    const { children, text, iconLeft, iconRight } = this.props
    const IconLeft = iconLeft ? iconLeft : null
    const IconRight = iconRight ? iconRight : null

    return (
      <span className="button__inner">
        {IconLeft && <IconLeft className="button__icon button__icon--left" />}
        <span className="button__text">{children || text}</span>
        {IconRight && <IconRight className="button__icon button__icon--right" />}
      </span>
    )
  }

  render() {
    const { url = '', externalLink, type, role } = this.props
    const ariaExpanded = this.props['aria-expanded']
    const ariaControls = this.props['aria-controls']
    const sharedProps = {
      className: this.BEM(),
      onClick: this.handleClick,
      ...(role ? { role } : {}),
      ...(ariaExpanded !== undefined ? { 'aria-expanded': ariaExpanded } : {}),
      ...(ariaControls ? { 'aria-controls': ariaControls } : {}),
      ...(url && externalLink ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {}),
    }
    const LinkComponent = () => {
      return externalLink ? (
        <a {...sharedProps} href={url} target="_blank" rel="noopener noreferrer">
          {this.renderInnerButton()}
        </a>
      ) : this.props.data ? (
        <Link
          {...sharedProps}
          to={{
            pathname: url,
            state: {
              data: this.props.data,
            },
          }}
        >
          {this.renderInnerButton()}
        </Link>
      ) : (
        <Link {...sharedProps} to={url}>
          {this.renderInnerButton()}
        </Link>
      )
    }

    return url ? (
      LinkComponent()
    ) : (
      <button {...sharedProps} type={type} disabled={this.props.disabled}>
        {this.renderInnerButton()}
      </button>
    )
  }
}
